import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import QuotePage from './pages/QuotePage';
import LicensesPage from './pages/LicensesPage';
import TechnicalSupportPage from './pages/TechnicalSupportPage';
import ProcurementPage from './pages/ProcurementPage';
import AppsHostingPage from './pages/AppsHostingPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ServicesPage from './pages/ServicesPage';
import LiveChat from './components/LiveChat';
import PortalDashboard from './pages/portal/PortalDashboard';
import PortalQuotes from './pages/portal/PortalQuotes';
import PortalInvoices from './pages/portal/PortalInvoices';
import PortalLicenses from './pages/portal/PortalLicenses';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminQuotes from './pages/admin/AdminQuotes';
import AdminUsers from './pages/admin/AdminUsers';
import AdminInvoices from './pages/admin/AdminInvoices';
import AdminLicenses from './pages/admin/AdminLicenses';
import AdminContacts from './pages/admin/AdminContacts';
import AdminChat from './pages/admin/AdminChat';
import AdminChatbotConfig from './pages/admin/AdminChatbotConfig';
import AdminTeam from './pages/admin/AdminTeam';
import AdminProducts from './pages/admin/AdminProducts';
import NotFoundPage from './pages/NotFoundPage';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

function ProtectedRoute({ children, role }: { children: React.ReactNode; role?: string }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="loading-spinner" /></div>;
  if (!user) return <Navigate to="/login" />;
  if (role && user.role !== role) return <Navigate to="/" />;
  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<><Header /><HomePage /></>} />
      <Route path="/login" element={<><Header /><LoginPage /></>} />
      <Route path="/register" element={<><Header /><RegisterPage /></>} />
      <Route path="/quote" element={<QuotePage />} />
      <Route path="/licenses" element={<LicensesPage />} />
      <Route path="/technical-support" element={<TechnicalSupportPage />} />
      <Route path="/procurement" element={<ProcurementPage />} />
      <Route path="/apps-hosting" element={<AppsHostingPage />} />
      <Route path="/forgot-password" element={<ResetPasswordPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/portal" element={<ProtectedRoute><PortalDashboard /></ProtectedRoute>} />
      <Route path="/portal/quotes" element={<ProtectedRoute><PortalQuotes /></ProtectedRoute>} />
      <Route path="/portal/invoices" element={<ProtectedRoute><PortalInvoices /></ProtectedRoute>} />
      <Route path="/portal/licenses" element={<ProtectedRoute><PortalLicenses /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/quotes" element={<ProtectedRoute role="admin"><AdminQuotes /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute role="admin"><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/invoices" element={<ProtectedRoute role="admin"><AdminInvoices /></ProtectedRoute>} />
      <Route path="/admin/licenses" element={<ProtectedRoute role="admin"><AdminLicenses /></ProtectedRoute>} />
      <Route path="/admin/contacts" element={<ProtectedRoute role="admin"><AdminContacts /></ProtectedRoute>} />
      <Route path="/admin/chat" element={<ProtectedRoute role="admin"><AdminChat /></ProtectedRoute>} />
      <Route path="/admin/chatbot" element={<ProtectedRoute role="admin"><AdminChatbotConfig /></ProtectedRoute>} />
      <Route path="/admin/team" element={<ProtectedRoute role="admin"><AdminTeam /></ProtectedRoute>} />
      <Route path="/admin/products" element={<ProtectedRoute role="admin"><AdminProducts /></ProtectedRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function ConditionalLiveChat() {
  const location = useLocation();
  const isPortal = location.pathname.startsWith('/portal') || location.pathname.startsWith('/admin');
  if (isPortal) return null;
  return <LiveChat />;
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
            <ConditionalLiveChat />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
